import {
  Stack,
  Avatar,
  Divider,
  ListItem,
  Typography,
  ListItemText,
  LinearProgress,
  ListItemAvatar
} from "@material-ui/core";
import { db } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import useSettings from "hooks/useSettings";
import { ImageNotSupported, Notes } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { fDateTime } from "utils/formatTime";
import { GenericConverter } from "utils/firestoreConverters";
import { getFullName } from "utils/Haim/utils";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import type { ContactsLookup } from "hooks/contacts/useContactsLookup";
import type { BulkMessageSentMessage } from "../../../../../@types/sharedSchema";

export function ContactTableNestedRowSentMessages({
  contactPhone
}: {
  /** The contact's phone — i.e. its document id. `sentTo` is stored in the same form. */
  contactPhone: string | undefined;
}): JSX.Element {
  const [sentMessages, setSentMessages] = useState<BulkMessageSentMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const { t: translate } = useTranslation();

  const {
    user: { email: userEmail }
  } = useAuth();

  useEffect(() => {
    const getSentMessages = async (): Promise<void> => {
      setLoading(true);

      const sentMessagesFromContact = await db
        .collection(`dashboard-users-v2/${userEmail}/sentMessages`)
        .where("sentTo", "==", contactPhone)
        .orderBy("sentTime", "desc")
        .withConverter(GenericConverter<BulkMessageSentMessage>())
        .get();

      const sentMessagesData = sentMessagesFromContact.docs.map((doc) => doc.data());

      setSentMessages(sentMessagesData);

      setLoading(false);
    };

    void getSentMessages();
  }, [userEmail, contactPhone]);

  if (loading) return <LinearProgress />;

  if (!sentMessages.length)
    return (
      <Typography variant="body1">{translate("contactsPage.error.noMessagesSentFound")}</Typography>
    );

  return (
    <Stack divider={<Divider />}>
      {sentMessages.map((sentMessage) => (
        <SentMessageRow sentMessage={sentMessage} key={sentMessage.id} />
      ))}
    </Stack>
  );
}

type SentMessageProps = {
  sentMessage: BulkMessageSentMessage;
  /** The listing's list: each row also says who the message went to. */
  inListingPage?: boolean;
  /**
   * The LIST's `useContactsLookup`, for the recipient's name — pass it with `inListingPage`.
   * One call at the list for all its rows; a row never mounts a lookup of its own.
   */
  lookup?: ContactsLookup["lookup"];
};

export function SentMessageRow({
  inListingPage,
  lookup,
  sentMessage: { message, mediaLink, sentTime, sentTo }
}: SentMessageProps) {
  const { themeDirection } = useSettings();

  const isRTL = themeDirection === "rtl";

  // `sentTo` is the recipient's phone, i.e. the contact's document id. Not answered yet and
  // "not in the book" both leave the name empty, and the row shows the phone alone.
  const recipient = inListingPage ? lookup?.(sentTo) : undefined;

  const primaryText = message;

  const contactFullName = getFullName(recipient?.callerFirstName, recipient?.callerLastName);

  const nationalPhone = parsePhoneNumber(sentTo)?.formatNational() ?? sentTo;

  const subtitlePrimaryText = contactFullName || nationalPhone;
  const subtitleSecondaryText = contactFullName ? nationalPhone : null;

  const secondaryText = (
    <Stack>
      <Typography variant="body2">{fDateTime(sentTime, isRTL)}</Typography>
      {inListingPage && (
        <Stack direction="row" gap={1}>
          <Typography variant="body2" color="text.primary">
            {subtitlePrimaryText}
          </Typography>
          {subtitleSecondaryText && (
            <Typography variant="body2" color="text.secondary">
              ({subtitleSecondaryText})
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );

  return (
    <ListItem>
      <ListItemAvatar sx={{ alignSelf: "start" }}>
        {mediaLink ? (
          <Avatar src={mediaLink}>
            <ImageNotSupported />
          </Avatar>
        ) : (
          <Notes sx={{ width: 40, height: 40 }} />
        )}
      </ListItemAvatar>
      <ListItemText primary={primaryText} secondary={secondaryText} />
    </ListItem>
  );
}
